import { useRef, useState, useEffect, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Icon } from '@iconify/react';
import {
  Box,
  List,
  Badge,
  Button,
  IconButton,
  Divider,
  Typography,
  ListItem,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Skeleton,
  useTheme,
} from '@mui/material';
import useInfiniteScroll from 'react-infinite-scroll-hook';

import Scrollbar from 'components/Scrollbar';
import MenuPopover from 'components/MenuPopover';
import { MIconButton } from 'components/@material-extend';
import { useTranslation } from 'react-i18next';
import { useGetNotificationUnreadCount, useGetNotifications } from 'modules/notification/api/queries';
import { useDeleteNotificationAll, useMarkNotificationAsRead } from 'modules/notification/api/mutation';
import NotificationCard from 'modules/notification/components/notificationCard';
import { PATH_DASHBOARD } from 'routes/paths';
import useDeleteConfirmDialog from 'common/components/useDeleteConfirmDialog';
import { NotificationList } from 'modules/notification/api/types';

export default function NotificationsPopover() {
  const anchorRef = useRef(null);
  const theme = useTheme();
  const { t } = useTranslation('notification');

  const [open, setOpen] = useState(false);
  const [selectedFarmId, setSelectedFarmId] = useState('all');

  const { data: unreadCountData } = useGetNotificationUnreadCount();
  const { mutateAsync: deleteAllMutation } = useDeleteNotificationAll();
  const { mutateAsync: markAsReadMutation } = useMarkNotificationAsRead();

  const params =
    selectedFarmId === 'all' ? undefined : selectedFarmId === 'system' ? 'system' : String(selectedFarmId);

  const {
    data: notificationsData,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetNotifications(params);

  const isLoading = !notificationsData && isFetching;

  const [localNotifications, setLocalNotifications] = useState<NotificationList[]>([]);

  const localNotificationsRef = useRef(localNotifications);
  useEffect(() => {
    localNotificationsRef.current = localNotifications;
  }, [localNotifications]);

  const markAsReadRef = useRef<((id: any) => Promise<void>) | null>(null);
  useEffect(() => {
    markAsReadRef.current = async (id) => {
      try {
        await markAsReadMutation([String(id)]);
      } catch (e) {

      }
      setLocalNotifications((prev) => prev.map((n) => (String(n.id) === String(id) ? { ...n, read_at: n.read_at ?? new Date().toISOString() } : n)));
    };
  }, [markAsReadMutation]);

  useEffect(() => {
    if (!notificationsData) return;
    const items = notificationsData.data ?? [];
    const unique = items.filter((v, i, a) => a.findIndex((x) => String(x.id) === String(v.id)) === i);
    const prevIds = localNotificationsRef.current.map((n) => String(n.id));
    const newIds = unique.map((n) => String(n.id));
    const sameLength = prevIds.length === newIds.length;
    const sameItems = sameLength && prevIds.every((id, idx) => id === newIds[idx]);
    if (sameItems) return;

    setLocalNotifications(unique);
  }, [notificationsData]);

  const observerRef = useRef<IntersectionObserver | null>(null);
  const timersRef = useRef<{ [key: string]: number }>({});
  const elementToIdMapRef = useRef(new Map());

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const id = elementToIdMapRef.current.get(entry.target);
          if (!id) return;

          const notif = localNotificationsRef.current.find((n) => String(n.id) === String(id));
          if (!notif || notif.read_at) {
            try {
              observerRef.current?.unobserve(entry.target);
            } catch (e) { }
            elementToIdMapRef.current.delete(entry.target);
            const t = timersRef.current[String(id)];
            if (t) {
              clearTimeout(t);
              delete timersRef.current[String(id)];
            }
            return;
          }

          if (entry.isIntersecting && entry.intersectionRatio > 0.5) {
            if (!timersRef.current[String(id)]) {
              timersRef.current[String(id)] = window.setTimeout(() => {
                if (markAsReadRef.current) markAsReadRef.current(id);
                delete timersRef.current[String(id)];
                try {
                  observerRef.current?.unobserve(entry.target);
                } catch (e) { }
                elementToIdMapRef.current.delete(entry.target);
              }, 2000);
            }
          } else {
            const t = timersRef.current[String(id)];
            if (t) {
              clearTimeout(t);
              delete timersRef.current[String(id)];
            }
          }
        });
      },
      { threshold: [0.5] }
    );

    return () => {
      try {
        observerRef.current?.disconnect();
      } catch (e) { }
      Object.values(timersRef.current).forEach((t) => clearTimeout(t));
      timersRef.current = {};
      elementToIdMapRef.current.clear();
    };
  }, []);

  const attachObserverFor = useCallback((el, id, readAt) => {
    if (!observerRef.current) return;
    if (!el) {
      for (const [element, value] of elementToIdMapRef.current.entries()) {
        if (String(value) === String(id)) {
          try {
            observerRef.current.unobserve(element);
          } catch (e) { }
          elementToIdMapRef.current.delete(element);
          break;
        }
      }
      return;
    }

    if (readAt) return;

    elementToIdMapRef.current.set(el, id);
    try {
      observerRef.current.observe(el);
    } catch (e) { }
  }, []);

  const { dialog, openDialog } = useDeleteConfirmDialog({
    onConfirm: async () => {
      try {
        await deleteAllMutation(params as any);
      } catch (e) { }
    },
  });

  const handleOpen = () => {
    setSelectedFarmId('all');
    setOpen(true);
    setLocalNotifications([]);
  };
  const handleClose = () => setOpen(false);

  const [sentryRef] = useInfiniteScroll({
    loading: Boolean(isFetchingNextPage || isFetching),
    hasNextPage: Boolean(hasNextPage),
    onLoadMore: () => fetchNextPage(),
    rootMargin: '0px 0px 200px 0px',
  });

  return (
    <>
      <MIconButton ref={anchorRef} size="large" color={open ? 'primary' : 'default'} onClick={handleOpen}>
        <Badge
          badgeContent={unreadCountData?.total_unread}
          color="error"
          overlap="circular"
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          sx={{
            '& .MuiBadge-badge': {
              transform: 'translate(70%, -70%)',
              fontSize: 12,
              lineHeight: '18px',
              boxShadow: `0 0 0 2px ${theme.palette.background.paper || '#fff'}`,
            },
          }}
        >
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
            <img src="/static/icons/layout/bell-notification.svg" width={20} height={20} alt="notifications" />
          </Box>
        </Badge>
      </MIconButton>

      <MenuPopover open={open} onClose={handleClose} anchorEl={anchorRef.current} sx={{ width: 380, maxWidth: '100%', p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <IconButton
            onClick={handleClose}
            disableRipple
            component={RouterLink}
            to={PATH_DASHBOARD.notification.setting}
          >
            <Icon icon="ant-design:setting-outlined" width="24" height="24" />
          </IconButton>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>{t('title')}</Typography>
          <IconButton size="small" disabled={isLoading} onClick={openDialog}>
            <Icon icon="mingcute:delete-2-line" width="24" height="24" />
          </IconButton>
        </Box>

        <Divider />

        <Scrollbar sx={{ maxHeight: { xs: '60vh', sm: '60vh' }, overflow: 'auto' }}>
          <FormControl size="small" sx={{ my: 2, width: '100%' }}>
            <InputLabel id="notification-filter-label">{t('filter')}</InputLabel>
            <Select
              labelId="notification-filter-label"
              value={selectedFarmId}
              label={t('filter')}
              onChange={(e) => {
                const value = e.target.value;
                setSelectedFarmId(value);
                setLocalNotifications([]);
              }}
            >
              <MenuItem value="all">{t('all')}</MenuItem>
              {unreadCountData?.items?.map((opt) => {
                const value = opt.name === 'system' ? 'system' : String(opt.id);
                return (
                  <MenuItem key={opt.id ?? opt.name} value={value}>
                    {`${opt.name === 'system' ? 'ระบบ' : opt.name} (${opt.unread})`}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          {localNotifications.length === 0 && isLoading && (
            <Box sx={{ p: 2 }}>
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} variant="rectangular" height={70} sx={{ mb: 1, borderRadius: 1 }} />
              ))}
            </Box>
          )}

          {!isLoading && localNotifications.length === 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', my: 4 }}>
              <img src="/static/icons/ci_no_data.svg" alt="" />
              <Typography color="text.secondary" variant="h6">{t('no_notifications')}</Typography>
            </Box>
          )}

          {localNotifications.length > 0 && (
            <>
              <List disablePadding>
                {localNotifications.map((n) => (
                  <ListItem
                    sx={{ mb: 2 }}
                    key={n.id}
                    disablePadding
                    ref={(el) => attachObserverFor(el, n.id, n.read_at ?? null)}
                  >
                    <NotificationCard
                      farm_name={n.farm_id ? n.farm_name : 'ระบบ'}
                      title={n.title}
                      body={n.body}
                      created_at={n.created_at}
                      read_at={n.read_at ?? null}
                      lines={2}
                    />
                  </ListItem>
                ))}
              </List>

              {isFetchingNextPage && localNotifications.length > 0 && (
                <Box sx={{ p: 1 }}>
                  <Skeleton variant="rectangular" height={60} sx={{ mb: 1, borderRadius: 1 }} />
                </Box>
              )}

              {hasNextPage && <div ref={sentryRef} />}
            </>
          )}
        </Scrollbar>

        <Divider />

        <Box sx={{ pt: 2 }}>
          <Button variant="contained" onClick={handleClose} fullWidth disableRipple component={RouterLink} to={PATH_DASHBOARD.notification.root}>
            {t('viewAll')}
          </Button>
        </Box>
        {dialog}
      </MenuPopover>
    </>
  );
}
